import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from '../../styles/MateriasStyles/PantallaDeTareas.v2.module.css';
import Pagination from '../../components/Pagination.tsx';
import { usePermissions } from '../../hooks/usePermissions';

const TableListaAsistencia = ({ query = '', asistencia = [], onView, onEdit, onDelete, loading = false, isSearching = false }) => {
  const filtered = asistencia || [];
  const { canEdit, canDelete } = usePermissions();
  const puedeEditar = canEdit('Otros');
  const puedeEliminar = canDelete('Otros');

  const [page, setPage] = useState(1);
  const pageSize = 8;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Crear clave única basada en IDs para detectar cambios reales
  const asistenciaKey = useMemo(() => {
    return (asistencia || []).map(a => a.id).sort().join(',');
  }, [asistencia]);

  const prevAsistenciaKeyRef = useRef(asistenciaKey);

  // Reiniciar página solo cuando realmente cambia la lista
  useEffect(() => {
    if (prevAsistenciaKeyRef.current !== asistenciaKey) {
      setPage(1);
      prevAsistenciaKeyRef.current = asistenciaKey;
    }
  }, [asistenciaKey]);

  // Ajustar página si quedó fuera de rango
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const ActionButtons = ({ registro }) => (
    <div className={styles.actionButtons}>
      <button aria-label="Ver detalles" className={styles.actionButton} onClick={() => onView && onView(registro)}>
        <img src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/a1782ac5b190a9d29d8f798383eedda3f294d56b?placeholderIfAbsent=true" alt="Ver" className={styles.actionIcon} />
      </button>
      {puedeEditar && (
        <button aria-label="Editar" className={styles.actionButton} onClick={() => onEdit && onEdit(registro)}>
          <img src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true" alt="Editar" className={styles.actionIcon} />
        </button>
      )}
      {puedeEliminar && (
        <button aria-label="Eliminar" className={styles.actionButton} onClick={() => onDelete && onDelete(registro)}>
          <img src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true" alt="Eliminar" className={styles.actionIcon} />
        </button>
      )}
      {!puedeEditar && !puedeEliminar && (
        <span style={{ color: '#888', fontSize: '12px' }}>Solo lectura</span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            <div className={styles.tableTitleIcon}>
              <div className={styles.iconContainerOrange}>
                <span>📋</span>
              </div>
            </div>
            <h2 className={styles.tableTitleText}>Lista de Asistencia</h2>
          </div>
        </div>
        <div className={styles.tableHeaderRow}>
          <span className={styles.columnHeader}>Estudiante</span>
          <span className={styles.columnHeader}>Matrícula</span>
          <span className={styles.columnHeader}>Grupo</span>
          <span className={styles.columnHeader}>Materia</span>
          <span className={styles.columnHeader}>Asesor</span>
          <span className={styles.columnHeader}>Modalidad</span>
          <span className={styles.columnHeader}>Turno</span>
          <span className={styles.columnHeader}>Días</span>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>Cargando asistencia...</div>
      </div>
    );
  }

  if (!paginated.length) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            <div className={styles.tableTitleIcon}>
              <div className={styles.iconContainerOrange}>
                <span>📋</span>
              </div>
            </div>
            <h2 className={styles.tableTitleText}>Lista de Asistencia</h2>
          </div>
        </div>
        <div className={styles.tableHeaderRow}>
          <span className={styles.columnHeader}>Estudiante</span>
          <span className={styles.columnHeader}>Matrícula</span>
          <span className={styles.columnHeader}>Grupo</span>
          <span className={styles.columnHeader}>Materia</span>
          <span className={styles.columnHeader}>Asesor</span>
          <span className={styles.columnHeader}>Modalidad</span>
          <span className={styles.columnHeader}>Turno</span>
          <span className={styles.columnHeader}>Días</span>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {isSearching
            ? `Sin resultados para “${query}”.`
            : 'No hay registros de asistencia.'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer} style={{ position: 'relative', minHeight: '320px', zIndex: 1 }}>
      <div className={styles.tableHeader}>
        <div className={styles.tableTitle}>
          <div className={styles.tableTitleIcon}>
            <div className={styles.iconContainerOrange}>
              <span>📋</span>
            </div>
          </div>
          <h2 className={styles.tableTitleText}>Lista de Asistencia</h2>
        </div>
      </div>

      <div className={styles.tableHeaderRow}>
        <span className={styles.columnHeader}>Estudiante</span>
        <span className={styles.columnHeader}>Matrícula</span>
        <span className={styles.columnHeader}>Grupo</span>
        <span className={styles.columnHeader}>Materia</span>
        <span className={styles.columnHeader}>Asesor</span>
        <span className={styles.columnHeader}>Modalidad</span>
        <span className={styles.columnHeader}>Turno</span>
        <span className={styles.columnHeader}>Días</span>
      </div>

      <div className={styles.tableBody}>
        {paginated.map((registro, index) => (
          <div key={registro.id || index} className={styles.tableRow}>
            <span className={styles.cellData}>{registro.estudiante || '—'}</span>
            <span className={styles.cellData}>{registro.matricula || '—'}</span>
            <span className={styles.cellData}>{registro.grupo || '—'}</span>
            <span className={styles.cellData}>{registro.materia || '—'}</span>
            <span className={styles.cellData}>{registro.asesor || '—'}</span>
            <span className={styles.cellData}>{registro.modalidad || '—'}</span>
            <span className={styles.cellData}>{registro.turno || '—'}</span>
            <span className={styles.cellData}>{registro.dias || '—'}</span>
          </div>
        ))}
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
};

export default React.memo(TableListaAsistencia);
